/**
 * Centralized Logger Service for ProDebtCo Lead Tracker
 *
 * Features:
 * - Batched log delivery to Supabase (50 logs or 5 seconds)
 * - Log levels: debug, info, warn, error
 * - Log categories: validation, button_state, component, api, analytics
 * - Automatic sensitive data sanitization
 * - Graceful degradation when offline/Supabase unavailable
 * - sendBeacon on page unload for reliable delivery
 */

import config from '../config/unified';

// Log levels
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

// Log categories - must match database CHECK constraint
export enum LogCategory {
  VALIDATION = 'validation',       // Form field validation events
  BUTTON_STATE = 'button_state',   // Next/submit button enable/disable
  COMPONENT = 'component',         // Component show/hide/rendering
  API = 'api',                     // API/webhook calls and responses
  ANALYTICS = 'analytics'          // GTM dataLayer, FB Pixel events
}

// Log entry interface for internal use
export interface LogEntry {
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

// Batched log for Supabase insertion
interface BatchedLog {
  session_id: string;
  log_level: string;
  category: string;
  message: string;
  data: Record<string, unknown>;
  page_url: string;
  environment: string;
  client_timestamp: string;
}

// Logger configuration
interface LoggerConfig {
  batchSize: number;
  flushIntervalMs: number;
  maxQueueSize: number;
  enableConsole: boolean;
  enableSupabase: boolean;
}

const DEFAULT_CONFIG: LoggerConfig = {
  batchSize: 50,
  flushIntervalMs: 5000,  // 5 seconds
  maxQueueSize: 500,
  enableConsole: true,
  enableSupabase: true    // Always send to Supabase (both envs)
};

// Level priority for filtering
const LEVEL_PRIORITY: Record<LogLevel, number> = {
  [LogLevel.DEBUG]: 0,
  [LogLevel.INFO]: 1,
  [LogLevel.WARN]: 2,
  [LogLevel.ERROR]: 3
};

// Sensitive field patterns to redact
const SENSITIVE_PATTERNS = [
  'ssn', 'social', 'password', 'pwd', 'token', 'key', 'secret', 'auth',
  'credit_card', 'creditcard', 'card_number', 'cvv', 'cvc', 'pin'
];

/**
 * Logger singleton class
 */
export class Logger {
  private static instance: Logger | null = null;
  private queue: BatchedLog[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private sessionId: string = '';
  private config: LoggerConfig;
  private isOnline: boolean = true;
  private failedFlushCount: number = 0;
  private readonly MAX_FAILED_FLUSHES = 5;
  private isInitialized: boolean = false;

  private constructor(customConfig?: Partial<LoggerConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...customConfig };
    this.setupNetworkListeners();
    this.startFlushTimer();
    this.isInitialized = true;
  }

  /**
   * Get singleton instance
   */
  public static getInstance(customConfig?: Partial<LoggerConfig>): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(customConfig);
    }
    return Logger.instance;
  }

  /**
   * Set session ID (called when session is created)
   */
  public setSessionId(sessionId: string): void {
    // Remove sess_ prefix if present for consistency with database
    this.sessionId = sessionId.replace(/^sess_/, '');

    if (config.debug) {
      console.log('[Logger] Session ID set:', this.sessionId.substring(0, 8) + '...');
    }
  }

  /**
   * Get current session ID
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  // ==================== Main Logging Methods ====================

  /**
   * Log debug message
   */
  public debug(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, category, message, data);
  }

  /**
   * Log info message
   */
  public info(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, category, message, data);
  }

  /**
   * Log warning message
   */
  public warn(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, category, message, data);
  }

  /**
   * Log error message
   */
  public error(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.ERROR, category, message, data);
  }

  // ==================== Core Logging Implementation ====================

  private log(level: LogLevel, category: LogCategory, message: string, data?: Record<string, unknown>): void {
    const timestamp = new Date().toISOString();

    // Console output (respects debug flag for non-error levels)
    if (this.config.enableConsole) {
      const shouldConsoleLog = config.debug || level === LogLevel.ERROR || level === LogLevel.WARN;
      if (shouldConsoleLog) {
        this.consoleLog(level, category, message, data);
      }
    }

    // Queue for Supabase
    if (this.config.enableSupabase && this.sessionId) {
      this.queueLog(level, category, message, data, timestamp);
    }
  }

  private consoleLog(level: LogLevel, category: LogCategory, message: string, data?: Record<string, unknown>): void {
    const prefix = this.getCategoryPrefix(category);
    const logArgs = data ? [`${prefix} ${message}`, data] : [`${prefix} ${message}`];

    switch (level) {
      case LogLevel.DEBUG:
        console.debug(...logArgs);
        break;
      case LogLevel.INFO:
        console.log(...logArgs);
        break;
      case LogLevel.WARN:
        console.warn(...logArgs);
        break;
      case LogLevel.ERROR:
        console.error(...logArgs);
        break;
    }
  }

  private getCategoryPrefix(category: LogCategory): string {
    const prefixes: Record<LogCategory, string> = {
      [LogCategory.VALIDATION]: '[VAL]',
      [LogCategory.BUTTON_STATE]: '[BTN]',
      [LogCategory.COMPONENT]: '[CMP]',
      [LogCategory.API]: '[API]',
      [LogCategory.ANALYTICS]: '[GTM]'
    };
    return prefixes[category];
  }

  // ==================== Queue Management ====================

  private queueLog(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: Record<string, unknown>,
    timestamp?: string
  ): void {
    // Drop oldest if queue is full
    if (this.queue.length >= this.config.maxQueueSize) {
      this.queue.shift();
    }

    const log: BatchedLog = {
      session_id: this.sessionId,
      log_level: level,
      category: category,
      message: message.substring(0, 500), // Limit message length
      data: this.sanitizeData(data || {}),
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      environment: config.environment,
      client_timestamp: timestamp || new Date().toISOString()
    };

    this.queue.push(log);

    // Flush if batch size reached
    if (this.queue.length >= this.config.batchSize) {
      this.flush();
    }
  }

  /**
   * Sanitize data to remove sensitive information
   */
  private sanitizeData(data: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();

      // Redact sensitive fields
      if (SENSITIVE_PATTERNS.some(pattern => lowerKey.includes(pattern))) {
        sanitized[key] = '[REDACTED]';
        continue;
      }

      // Handle different value types
      if (value === null || value === undefined) {
        sanitized[key] = value;
      } else if (typeof value === 'string') {
        // Truncate long strings
        sanitized[key] = value.length > 500 ? value.substring(0, 500) + '...[truncated]' : value;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        sanitized[key] = value;
      } else if (Array.isArray(value)) {
        // Truncate arrays
        sanitized[key] = value.length > 10
          ? [...value.slice(0, 10), `...(${value.length - 10} more)`]
          : value;
      } else if (typeof value === 'object') {
        // Stringify nested objects (shallow)
        try {
          const stringified = JSON.stringify(value);
          sanitized[key] = stringified.length > 1000
            ? stringified.substring(0, 1000) + '...[truncated]'
            : value;
        } catch {
          sanitized[key] = '[Object - stringify failed]';
        }
      } else {
        sanitized[key] = String(value);
      }
    }

    return sanitized;
  }

  // ==================== Flush Management ====================

  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushTimer = setInterval(() => this.flush(), this.config.flushIntervalMs);
  }

  /**
   * Flush queued logs to Supabase
   */
  public async flush(): Promise<void> {
    if (this.queue.length === 0) {
      return;
    }

    if (!this.isOnline) {
      if (config.debug) {
        console.debug('[Logger] Offline - skipping flush');
      }
      return;
    }

    // Check if we've failed too many times
    if (this.failedFlushCount >= this.MAX_FAILED_FLUSHES) {
      if (config.debug) {
        console.warn('[Logger] Too many failed flushes, Supabase logging disabled until page reload');
      }
      return;
    }

    const batch = [...this.queue];
    this.queue = [];

    try {
      const response = await fetch(`${config.supabase.url}/rest/v1/simple_logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.supabase.anonKey,
          'Authorization': `Bearer ${config.supabase.anonKey}`,
          'Prefer': 'return=minimal'
        },
        credentials: 'omit', // Don't send cookies - using API key auth
        body: JSON.stringify(batch)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.failedFlushCount = 0; // Reset on success

      if (config.debug) {
        console.debug(`[Logger] Flushed ${batch.length} logs to Supabase`);
      }
    } catch (error) {
      this.failedFlushCount++;
      // Re-queue failed logs (at front, limited by max queue size)
      this.queue = [...batch, ...this.queue].slice(0, this.config.maxQueueSize);

      if (config.debug) {
        console.warn(`[Logger] Flush failed (${this.failedFlushCount}/${this.MAX_FAILED_FLUSHES}):`, error);
      }
    }
  }

  /**
   * Force flush using fetch with keepalive (for page unload)
   * Note: sendBeacon always sends credentials which conflicts with Supabase wildcard CORS
   */
  public forceFlush(): void {
    if (this.queue.length === 0) {
      return;
    }

    const batch = [...this.queue];
    this.queue = [];

    try {
      // Use fetch with keepalive for reliability on page unload
      // Cannot use sendBeacon - it always sends credentials which causes CORS errors with Supabase
      const url = `${config.supabase.url}/rest/v1/simple_logs`;

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.supabase.anonKey,
          'Authorization': `Bearer ${config.supabase.anonKey}`,
          'Prefer': 'return=minimal'
        },
        credentials: 'omit', // REQUIRED - Supabase uses wildcard CORS
        keepalive: true, // Allows request to outlive the page
        body: JSON.stringify(batch)
      }).catch(() => {
        // Silently fail on page unload - nothing we can do
      });

      if (config.debug) {
        console.debug(`[Logger] forceFlush: ${batch.length} logs (keepalive fetch)`);
      }
    } catch (error) {
      if (config.debug) {
        console.warn('[Logger] forceFlush failed:', error);
      }
    }
  }

  // ==================== Network & Lifecycle ====================

  private setupNetworkListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('online', () => {
      this.isOnline = true;
      if (config.debug) {
        console.debug('[Logger] Online - resuming log delivery');
      }
      this.flush();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      if (config.debug) {
        console.debug('[Logger] Offline - pausing log delivery');
      }
    });

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.forceFlush();
    });

    // Also flush on visibility change (tab hidden)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.forceFlush();
      }
    });
  }

  /**
   * Get queue status for debugging
   */
  public getStatus(): { queueLength: number; isOnline: boolean; failedFlushes: number; sessionId: string } {
    return {
      queueLength: this.queue.length,
      isOnline: this.isOnline,
      failedFlushes: this.failedFlushCount,
      sessionId: this.sessionId ? this.sessionId.substring(0, 8) + '...' : 'not set'
    };
  }

  /**
   * Reset failed flush counter (useful after connectivity recovery)
   */
  public resetFailedFlushes(): void {
    this.failedFlushCount = 0;
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

export default logger;
